import React, { useState } from "react";
import styled from "styled-components";
import { useCSVReader } from "react-papaparse";
import {
    SegmentedButtonGroup,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalErrorText,
    StandardModalHelperText,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionTitle,
    portalColors
} from "src/components";
import {
    CsvParseResults,
    DirectoryCsvRow,
    DirectoryCustomFieldDefinition,
    DirectoryVisibility,
    downloadDirectoryCsvTemplate,
    parseDirectoryCsv
} from "../directoryCsv";

interface UploadDirectoryCsvModalProps {
    open: boolean;
    customFields: DirectoryCustomFieldDefinition[];
    employeeLabel: string;
    clientLabel: string;
    onBack: () => void;
    onClose: () => void;
    onPreview: (rows: DirectoryCsvRow[]) => void;
}

interface CsvReaderRenderProps {
    getRootProps: () => Record<string, unknown>;
}

const UploadSteps = styled.div`
    display: grid;
    gap: 1rem;
`;

const Step = styled(StandardModalSection)`
    gap: 0.65rem;
`;

const VisibilityControl = styled.div`
    width: min(100%, 39rem);
`;

const UploadControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const FilePill = styled.div`
    min-width: 0;
    max-width: 22rem;
    padding: 0.7rem 0.85rem;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    font-size: 0.875rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Reminder = styled.div`
    display: grid;
    gap: 0.3rem;
    padding: 0.9rem 1rem;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    background: ${portalColors.surfaceSoft};
`;

const UploadDirectoryCsvModal = ({
    open,
    customFields,
    employeeLabel,
    clientLabel,
    onBack,
    onClose,
    onPreview
}: UploadDirectoryCsvModalProps): React.JSX.Element => {
    const { CSVReader } = useCSVReader();
    const [visibility, setVisibility] = useState<DirectoryVisibility>("employee");
    const [fileName, setFileName] = useState("");
    const [parseResults, setParseResults] = useState<CsvParseResults | null>(null);
    const [errors, setErrors] = useState<string[]>([]);

    const handleNext = (): void => {
        if (!parseResults) {
            setErrors(["Please choose a CSV file to continue."]);
            return;
        }

        const parsed = parseDirectoryCsv(parseResults, visibility, customFields);
        setErrors(parsed.errors);
        if (parsed.errors.length === 0) {
            onPreview(parsed.rows);
        }
    };

    return (
        <StandardModal
            open={open}
            onClose={onClose}
            widthMode="wide"
            title="Upload Multiple Listings via CSV"
            description="Choose who can see these listings, download the current Directory template, then upload your completed CSV file."
            footer={(
                <StandardModalActionGroup style={{ marginLeft: "auto" }}>
                    <StandardModalActionButton width="140px" onClick={onBack}>
                        Back
                    </StandardModalActionButton>
                    <StandardModalActionButton $variant="primary" width="140px" onClick={handleNext}>
                        Next
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <UploadSteps>
                <Step>
                    <StandardModalSectionTitle>Step 1 · Visible To</StandardModalSectionTitle>
                    <StandardModalDescription>
                        Select the initial audience for every listing. You can change individual rows in the preview.
                    </StandardModalDescription>
                    <VisibilityControl>
                        <SegmentedButtonGroup
                            options={[
                                { value: "employee", label: employeeLabel },
                                { value: "client", label: clientLabel },
                                { value: "employee_and_client", label: `${employeeLabel} & ${clientLabel}` }
                            ]}
                            value={visibility}
                            onChange={(value: DirectoryVisibility) => setVisibility(value)}
                            disabled={false}
                            minWidth="0"
                            equalWidth
                        />
                    </VisibilityControl>
                </Step>

                <Step>
                    <StandardModalSectionTitle>Step 2 · Download Directory CSV Template</StandardModalSectionTitle>
                    <StandardModalDescription>
                        The template contains the standard Directory columns and your current custom fields.
                    </StandardModalDescription>
                    <StandardModalActionButton
                        $variant="primary"
                        width="225px"
                        onClick={() => downloadDirectoryCsvTemplate(customFields)}
                    >
                        Download CSV Template
                    </StandardModalActionButton>
                </Step>

                <Step>
                    <StandardModalSectionTitle>Step 3 · Upload CSV File</StandardModalSectionTitle>
                    <StandardModalLabel>Completed Directory CSV</StandardModalLabel>
                    <UploadControls>
                        <CSVReader
                            config={{ header: true, skipEmptyLines: true }}
                            onUploadAccepted={(results: CsvParseResults, file: File) => {
                                setParseResults(results);
                                setFileName(file.name);
                                setErrors([]);
                            }}
                        >
                            {({ getRootProps }: CsvReaderRenderProps) => (
                                <StandardModalActionButton
                                    {...getRootProps()}
                                    $variant="primary"
                                    width="160px"
                                >
                                    Browse File
                                </StandardModalActionButton>
                            )}
                        </CSVReader>
                        {fileName && <FilePill title={fileName}>{fileName}</FilePill>}
                    </UploadControls>
                    {errors.map(error => <StandardModalErrorText key={error}>{error}</StandardModalErrorText>)}
                </Step>

                <Reminder>
                    <StandardModalLabel>Directory images</StandardModalLabel>
                    <StandardModalHelperText>
                        Images are not imported from CSV. Add an image by editing a listing after the import is saved.
                    </StandardModalHelperText>
                </Reminder>
            </UploadSteps>
        </StandardModal>
    );
};

export default UploadDirectoryCsvModal;
